import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { patch, post } from '../api';
import { Empty, ErrorBox, Loading, Modal, PageHeader, useToast } from '../components/ui';
import { fmtDate, fmtNumber } from '../format';
import type { Group } from '../types';
import { useGroups } from './shared';

export function GroupsPage() {
  const groups = useGroups();
  const [editing, setEditing] = useState<Group | 'new' | null>(null);

  return (
    <>
      <PageHeader
        title="Grupos"
        subtitle="Cada grupo tiene sus propios usuarios, API keys, trabajos y cuota"
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Plus size={16} /> Nuevo grupo
          </button>
        }
      />
      <div className="card">
        <ErrorBox error={groups.error} />
        {groups.isLoading ? (
          <Loading />
        ) : !groups.data?.length ? (
          <Empty icon={Users} title="Todavía no hay grupos">
            Crea el primero para dar de alta usuarios y API keys.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Estado</th>
                  <th className="num">Cuota mensual</th>
                  <th className="num">Retención</th>
                  <th>Creado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.data.map((g) => (
                  <tr key={g.id}>
                    <td className="cell-main">{g.name}</td>
                    <td>{g.active ? <span className="badge badge-done">Activo</span> : <span className="badge badge-cancelled">Desactivado</span>}</td>
                    <td className="num">{g.monthlyPageQuota ? `${fmtNumber(g.monthlyPageQuota)} págs.` : <span className="faint">Sin límite</span>}</td>
                    <td className="num">{g.retentionHours ? `${g.retentionHours} h` : <span className="faint">Por defecto</span>}</td>
                    <td>{fmtDate(g.createdAt)}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn btn-icon" onClick={() => setEditing(g)} aria-label="Editar">
                          <Pencil size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && <GroupModal group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function GroupModal({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(group?.name ?? '');
  const [quota, setQuota] = useState(group?.monthlyPageQuota?.toString() ?? '');
  const [retention, setRetention] = useState(group?.retentionHours?.toString() ?? '');
  const [active, setActive] = useState(group?.active ?? true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name,
      active,
      monthlyPageQuota: quota ? Number(quota) : null,
      retentionHours: retention ? Number(retention) : null,
    };
    try {
      if (group) await patch(`/groups/${group.id}`, body);
      else await post('/groups', body);
      await qc.invalidateQueries({ queryKey: ['groups'] });
      toast(group ? 'Grupo actualizado' : 'Grupo creado');
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={group ? 'Editar grupo' : 'Nuevo grupo'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary" form="group-form" disabled={busy}>
            {busy && <Loader2 size={14} className="spin" />}
            Guardar
          </button>
        </>
      }
    >
      <form id="group-form" onSubmit={submit}>
        <ErrorBox error={error} />
        <div className="field">
          <label htmlFor="g-name">Nombre</label>
          <input id="g-name" className="input" required minLength={2} maxLength={120} autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="g-quota">Cuota mensual (páginas)</label>
            <input id="g-quota" className="input" type="number" min={1} placeholder="Sin límite" value={quota} onChange={(e) => setQuota(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="g-retention">Retención de archivos (horas)</label>
            <input id="g-retention" className="input" type="number" min={1} placeholder="Por defecto" value={retention} onChange={(e) => setRetention(e.target.value)} />
          </div>
        </div>
        {group && (
          <label className="checkbox">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            <span>
              Grupo activo
              <span className="faint"> — desactivarlo bloquea a sus usuarios y API keys</span>
            </span>
          </label>
        )}
      </form>
    </Modal>
  );
}
